import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import BOImageCardGridSkeleton from "../gridCards/animations/BOImageCardGridSkeleton";
import { v4 as uuidv4 } from "uuid";
import * as Library from "../../../utils/Library";
import AppContext from "../../../AppContext";
import SidePanelLinkingCard from "../mainApp/SidePanelLinkingCard";

/* #region  SCOPED CSS */
const StyledPage = styled.div`
    & {
        overflow-y: scroll;
        //height: 100%;
        height: calc(100vh - 180px);
        padding-right: 7px;
    }
`;
/* #endregion */

/**
 * Renders an infinite grid of cards for the BO images.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The additional CSS class name for the component.
 * @param {number} [props.limit=10] - The maximum number of cards to load per page.
 * @param {string} [props.queryKey] - The query key for fetching data.
 * @param {number} [props.minimumCardsFill] - The minimum number of cards to fill the grid.
 * @param {number} [props.minColumnWidth] - The minimum width of each grid column.
 * @param {Function} [props.onEditAction] - The callback function for the edit action.
 * @param {Function} [props.onDeleteAction] - The callback function for the delete action.
 * @param {Function} [props.onSuspendAction] - The callback function for the suspend action.
 * * @param {string} [props.statusFromPath] - The status from the path.
 * @returns {JSX.Element} The rendered component.
 */

export default function InfinityGridSidePanelBOImages(props) {
    const gridLayoutStyle = {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${"100%"}, 1fr))`,
        gap: "8px"
    };

    /* #region STATES */
    const {
        setSidePanelIsLoading,
        sidePanelRetailBrandID,
        sidePanelProducts,
        setSidePanelProducts,
        sidePanelProductSearchName,
        setSidePanelHasNoProducts
    } = React.useContext(AppContext);

    const scrollContainerRef = useRef(null);
    const [scroll, setScroll] = useState(true);
    //const [queryKey, setQueryKey] = useState(props?.queryKey ?? uuidv4());
    //const [minimumCardsFill, setminimumCardsFill] = useState(props?.minimumCardsFill || pageLimit);

    const pageLimit = 10;
    const queryKey = ["getProductsForBOimages", sidePanelRetailBrandID, sidePanelProductSearchName];

    const {
        data,
        isFetching: isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = Library.usePostInfiniteQuery(
        queryKey,
        "getProductsForBOimages",
        {
            retailBrandID: sidePanelRetailBrandID,
            searchName: sidePanelProductSearchName,
            limit: pageLimit
        },
        () => {
            setScroll(true);
        },
        true,
        "BOImages"
    );

    const pageList = data?.pages;
    const nextDataCount = data?.pages[data?.pages.length - 1].data?.nextDataCount ?? pageLimit;
    /* #endregion */

    /* #region EFFECTS */
    useEffect(() => {
        handleScroll();

        if (!data?.pages) {
            setSidePanelProducts([]);
        }

        try {
            const newProducts = data?.pages?.reduce((acc, it) => {
                if (!it?.data?.result) return acc;
                return [...acc, ...it?.data?.result];
            }, []);

            setSidePanelProducts(newProducts);
        } catch (e) {
            console.error("Error in InfinityGridSidePanelBOImages useEffect: ", e);
            setSidePanelProducts([]);
        }
    }, [data?.pages]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && !isLoading && scroll && !isFetchingNextPage) {
            scrollContainer.addEventListener("scroll", handleScroll);
        }

        return () => {
            scrollContainer?.removeEventListener("scroll", handleScroll);
        };
    }, [scrollContainerRef, isLoading, pageList, scroll, isFetchingNextPage, data?.pages]);

    useEffect(() => {
        setSidePanelIsLoading(isLoading || isFetchingNextPage);
        if (isLoading || isFetchingNextPage) {
            setSidePanelHasNoProducts(false);
        } else if (sidePanelProducts?.length === 0) {
            setSidePanelHasNoProducts(true);
        } else if (sidePanelProducts?.length > 0) {
            setSidePanelHasNoProducts(false);
        }
    }, [sidePanelProducts, isLoading, isFetchingNextPage]);
    /* #endregion */

    /* #region METHODS */
    const handleScroll = () => {
        const scrollContainer = scrollContainerRef.current;

        const scrolledRatio = scrollContainer.scrollTop / (scrollContainer.scrollHeight - scrollContainer.clientHeight);
        const isAtBottom = scrolledRatio >= 0.6;

        if (isAtBottom && !isLoading && hasNextPage && !isFetchingNextPage) {
            setScroll(false);
            fetchNextPage();
        }
    };

    return (
        <StyledPage className={`library-scrollable ${props?.className || ""} w-100`} ref={scrollContainerRef}>
            <div className="mb-8px w-100" style={gridLayoutStyle}>
                {sidePanelProducts?.map(item => (
                    <div key={"SidePanelCard" + item.productID} className="ListItem2">
                        <SidePanelLinkingCard productID={item.productID} />
                    </div>
                ))}

                {(isLoading || isFetchingNextPage) &&
                    Array.from({ length: nextDataCount }, () => <BOImageCardGridSkeleton key={uuidv4()} />)}
            </div>
        </StyledPage>
    );
}
